import React, { useState, useEffect } from 'react';
import { authService } from '../services/api';
import { useDialog } from '../components/DialogContext';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'staff',
    status: 'active'
  });
  const currentUser = authService.getCurrentUser();
  const dialog = useDialog();

  const roles = [
    { value: 'super_super_admin', label: 'Super Super Admin', icon: '👑', color: '#dc2626' },
    { value: 'super_admin', label: 'Super Admin', icon: '⚡', color: '#ea580c' },
    { value: 'senior_nurse', label: 'Senior Nurse', icon: '🩺', color: '#8b5cf6' },
    { value: 'staff', label: 'Staff Member', icon: '👤', color: '#3b82f6' }
  ];

  useEffect(() => {
    loadUsers();
    
    // Handle window resize for responsive design
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://b1.envicblue.co.uk/api/users.php', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data);
      }
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://b1.envicblue.co.uk/api/users.php?action=create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await dialog.success({ 
          message: 'User added successfully! A setup link has been sent to their email.' 
        });
        setShowAddModal(false);
        loadUsers();
        resetForm();
      } else {
        await dialog.error({ 
          message: `Failed to add user: ${data.message}` 
        });
      }
    } catch (err) {
      await dialog.error({ 
        message: `Failed to add user: ${err.message}` 
      });
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`https://b1.envicblue.co.uk/api/users.php?action=update&id=${selectedUser.user_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await dialog.success({ 
          message: 'User updated successfully!' 
        });
        setShowEditModal(false);
        loadUsers();
        setSelectedUser(null);
      } else {
        await dialog.error({ 
          message: `Failed to update user: ${data.message}` 
        });
      }
    } catch (err) {
      await dialog.error({ 
        message: `Failed to update user: ${err.message}` 
        });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmed = await dialog.confirmDelete({
      title: 'Delete User',
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`
    });
    
    if (confirmed) {
      try {
        const response = await fetch(`https://b1.envicblue.co.uk/api/users.php?action=delete&id=${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          await dialog.success({ 
            message: 'User deleted successfully!' 
          });
          loadUsers();
        } else {
          await dialog.error({ 
            message: `Failed to delete user: ${data.message}` 
          });
        }
      } catch (err) {
        await dialog.error({ 
          message: `Failed to delete user: ${err.message}` 
        });
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const response = await fetch(`https://b1.envicblue.co.uk/api/users.php?action=toggle-status&id=${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const data = await response.json();
      
      if (data.success) {
        await dialog.success({ 
          message: `User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!` 
        });
        loadUsers();
      }
    } catch (err) {
      await dialog.error({ 
        message: `Failed to update status: ${err.message}` 
      });
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      role: 'staff',
      status: 'active'
    });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      status: user.status
    });
    setShowEditModal(true);
  };

  const getRoleInfo = (roleValue) => {
    return roles.find(r => r.value === roleValue) || roles[3];
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Calculate statistics
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
    admins: users.filter(u => ['super_super_admin', 'super_admin'].includes(u.role)).length
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⏳</div>
          <div style={{ color: '#666', marginTop: '10px' }}>Loading users...</div>
        </div>
      </div>
    );
  }

  // Mobile Card View Component
  const MobileUserCard = ({ user, index }) => {
    const roleInfo = getRoleInfo(user.role);
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '15px',
        marginBottom: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: `${roleInfo.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              flexShrink: 0
            }}>
              {roleInfo.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '600', color: '#333', fontSize: '15px', marginBottom: '2px' }}>
                {user.full_name}
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                ID: {user.user_id}
              </div>
            </div>
          </div>
          <span style={{
            padding: '4px 8px',
            background: user.status === 'active' ? '#10b98120' : '#ef444420',
            color: user.status === 'active' ? '#10b981' : '#ef4444',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>
            {user.status === 'active' ? '✅' : '⏸️'}
          </span>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', color: '#333', marginBottom: '4px', wordBreak: 'break-all' }}>
            📧 {user.email}
          </div>
          {user.phone && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              📱 {user.phone}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '5px 10px',
            background: `${roleInfo.color}20`,
            color: roleInfo.color,
            borderRadius: '16px',
            fontSize: '11px',
            fontWeight: '600'
          }}>
            {roleInfo.icon} {roleInfo.label}
          </span>
        </div>

        <div style={{ fontSize: '11px', color: '#999', marginBottom: '12px' }}>
          Joined: {new Date(user.created_at).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
        </div>

        {currentUser?.role === 'super_super_admin' && (
          <div style={{ display: 'flex', gap: '6px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
            <button
              onClick={() => openEditModal(user)}
              style={{
                flex: 1,
                padding: '8px',
                background: '#667eea20',
                color: '#667eea',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => handleToggleStatus(user.user_id, user.status)}
              style={{
                flex: 1,
                padding: '8px',
                background: user.status === 'active' ? '#ef444420' : '#10b98120',
                color: user.status === 'active' ? '#ef4444' : '#10b981',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              {user.status === 'active' ? '⏸️ Deactivate' : '▶️ Activate'}
            </button>
            <button
              onClick={() => handleDeleteUser(user.user_id, user.full_name)}
              style={{
                padding: '8px 12px',
                background: '#ef444420',
                color: '#ef4444',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              🗑️
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '15px' : '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        marginBottom: '20px',
        gap: isMobile ? '15px' : '0'
      }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
            👥 Users Management
          </h1>
          <p style={{ color: '#666', fontSize: isMobile ? '13px' : '14px' }}>Manage staff members and permissions</p>
        </div>
        {currentUser?.role === 'super_super_admin' && (
          <button 
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: isMobile ? '10px 20px' : '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              fontSize: '14px',
              width: isMobile ? '100%' : 'auto'
            }}>
            ➕ Add New User
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: isMobile ? '13px' : '14px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: isMobile ? '12px' : '20px',
        marginBottom: isMobile ? '20px' : '30px'
      }}>
        {[
          { icon: '👥', label: 'Total Users', value: stats.total, color: '#667eea' },
          { icon: '✅', label: 'Active Users', value: stats.active, color: '#10b981' },
          { icon: '⏸️', label: 'Inactive Users', value: stats.inactive, color: '#ef4444' },
          { icon: '⚡', label: 'Administrators', value: stats.admins, color: '#8b5cf6' }
        ].map((stat, index) => (
          <div key={index} style={{
            background: 'white',
            padding: isMobile ? '15px' : '20px',
            borderRadius: '16px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px' }}>{stat.icon}</div>
            <div style={{ color: '#666', fontSize: isMobile ? '12px' : '14px', marginBottom: '5px' }}>{stat.label}</div>
            <div style={{ fontSize: isMobile ? '24px' : '28px', fontWeight: 'bold', color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        padding: isMobile ? '15px' : '20px',
        borderRadius: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              🔍 Search Users
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              👤 Filter by Role
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              <option value="all">All Roles</option>
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              ⚡ Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List - Mobile or Desktop */}
      {isMobile ? (
        /* Mobile View - Cards */
        <div>
          {filteredUsers.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px 20px',
              textAlign: 'center',
              color: '#999',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
              <div>No users found</div>
            </div>
          ) : (
            filteredUsers.map((user, index) => (
              <MobileUserCard key={user.user_id} user={user} index={index} />
            ))
          )}
        </div>
      ) : (
        /* Desktop View - Table */
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                    User
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                    Contact
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                    Role
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                    Status
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                    Joined
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
                      <div>No users found</div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => {
                    const roleInfo = getRoleInfo(user.role);
                    return (
                      <tr key={user.user_id} style={{
                        borderBottom: '1px solid #f0f0f0',
                        background: index % 2 === 0 ? 'white' : '#fafafa',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5ff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafafa'}>
                        <td style={{ padding: '15px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '45px',
                              height: '45px',
                              borderRadius: '50%',
                              background: `${roleInfo.color}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px'
                            }}>
                              {roleInfo.icon}
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: '#333', marginBottom: '3px' }}>
                                {user.full_name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#999' }}>
                                ID: {user.user_id}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td style={{ padding: '15px' }}>
                          <div style={{ fontSize: '14px', color: '#333', marginBottom: '3px' }}>
                            📧 {user.email}
                          </div>
                          {user.phone && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              📱 {user.phone}
                            </div>
                          )}
                        </td>

                        <td style={{ padding: '15px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: `${roleInfo.color}20`,
                            color: roleInfo.color,
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {roleInfo.icon} {roleInfo.label}
                          </span>
                        </td>

                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          <span style={{
                            padding: '6px 12px',
                            background: user.status === 'active' ? '#10b98120' : '#ef444420',
                            color: user.status === 'active' ? '#10b981' : '#ef4444',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {user.status === 'active' ? '✅ Active' : '⏸️ Inactive'}
                          </span>
                        </td>

                        <td style={{ padding: '15px' }}>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            {new Date(user.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        </td>

                        <td style={{ padding: '15px' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            {currentUser?.role === 'super_super_admin' && (
                              <>
                                <button
                                  onClick={() => openEditModal(user)}
                                  title="Edit User"
                                  style={{
                                    padding: '8px 12px',
                                    background: '#667eea20',
                                    color: '#667eea',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✏️
                                </button>

                                <button
                                  onClick={() => handleToggleStatus(user.user_id, user.status)}
                                  title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                                  style={{
                                    padding: '8px 12px',
                                    background: user.status === 'active' ? '#ef444420' : '#10b98120',
                                    color: user.status === 'active' ? '#ef4444' : '#10b981',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {user.status === 'active' ? '⏸️' : '▶️'}
                                </button>

                                <button
                                  onClick={() => handleDeleteUser(user.user_id, user.full_name)}
                                  title="Delete User"
                                  style={{
                                    padding: '8px 12px',
                                    background: '#ef444420',
                                    color: '#ef4444',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                  }}
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '15px' : '20px'
        }}
        onClick={() => setShowAddModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '30px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: isMobile ? '90vh' : '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#333' }}>
              ➕ Add New User
            </h2>

            <form onSubmit={handleAddUser}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="John Smith"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="john.smith@b1cardiology.com"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="+44 7700 900000"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.icon} {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{
                background: '#f0f7ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '12px', color: '#1e40af', lineHeight: '1.6' }}>
                  ℹ️ <strong>Note:</strong> A unique setup link will be sent to the user's email address.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f0f0f0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ✅ Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '15px' : '20px'
        }}
        onClick={() => setShowEditModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '30px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: isMobile ? '90vh' : '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#333' }}>
              ✏️ Edit User
            </h2>

            <form onSubmit={handleEditUser}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.icon} {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f0f0f0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ✅ Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;